import {
  closingValues,
  isAnnouncementCurrent,
} from '@/announcement/announcement'
import Loading from '@/common/atoms/Loading'
import TextError from '@/common/atoms/TextError'
import useConfirmModal from '@/common/hooks/useConfirmModal'
import {
  Button,
  Card,
  CardBody,
  Flex,
  Heading,
  Table,
  TableContainer,
  Tbody,
  Text,
  Th,
  Thead,
  Tr,
  useDisclosure,
  useToast,
} from '@chakra-ui/react'
import {
  AnnouncementFragment,
  useAdminAnnouncementsSubscription,
  useAdminCloseAnnouncementsMutation,
  useAdminCreateAnnouncementMutation,
  useAdminDeleteAnnouncementMutation,
  useAdminUpdateAnnouncementMutation,
} from '@gql'
import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { AddIcon } from 'src/icons'
import AdminAnnouncementModal, {
  AnnouncementValues,
} from '../components/AdminAnnouncementModal'
import AdminAnnouncementRow from '../components/AdminAnnouncementRow'

// #77 — the console's « Annonces » page: the current announcement first, then
// the past ones, greyed. One announcement at a time: publishing a new one
// closes those still running.
export default function AdminAnnouncementsPage() {
  const { t } = useTranslation()
  const toast = useToast()
  const newModal = useDisclosure()
  const [editing, setEditing] = useState<AnnouncementFragment>()
  const { confirm, confirmElement } = useConfirmModal()

  const { data, loading, error } = useAdminAnnouncementsSubscription()
  const [createAnnouncement] = useAdminCreateAnnouncementMutation()
  const [updateAnnouncement] = useAdminUpdateAnnouncementMutation()
  const [closeStarted] = useAdminCloseAnnouncementsMutation()
  const [deleteAnnouncement] = useAdminDeleteAnnouncementMutation()

  const now = new Date()
  const announcements = data?.announcement ?? []

  const run = async (action: () => Promise<unknown>) => {
    try {
      await action()
    } catch (error: any) {
      toast({
        title: t('common.error'),
        description: error?.message,
        status: 'error',
      })
      throw error
    }
  }

  const handleCreate = (values: AnnouncementValues) =>
    run(async () => {
      const closeAt = new Date()
      // Those already started take `now` as their end in one go; one still to
      // start also has its start moved (see closingValues).
      await closeStarted({ variables: { now: closeAt.toISOString() } })
      for (const running of announcements) {
        if (
          isAnnouncementCurrent(running, closeAt) &&
          new Date(running.startsAt) >= closeAt
        ) {
          await updateAnnouncement({
            variables: {
              id: running.id,
              values: closingValues(running, closeAt),
            },
          })
        }
      }
      await createAnnouncement({ variables: { values } })
    })

  const handleUpdate = (id: string) => (values: AnnouncementValues) =>
    run(() => updateAnnouncement({ variables: { id, values } }))

  const handleDelete = (announcement: AnnouncementFragment) =>
    confirm({
      heading: t('SuperAdmin.announcements.deleteHeading'),
      info: announcement.message,
      buttonColor: 'red',
      buttonLabel: t('common.delete'),
      onConfirm: () =>
        run(() =>
          deleteAnnouncement({ variables: { id: announcement.id } })
        ).catch(() => {}),
    })

  return (
    <>
      <Flex mb={6} align="center" gap={3} wrap="wrap">
        <Heading size="md">{t('SuperAdmin.announcements.heading')}</Heading>
        <Button
          ml="auto"
          size="sm"
          colorScheme="blue"
          leftIcon={<AddIcon size={20} />}
          onClick={newModal.onOpen}
        >
          {t('SuperAdmin.announcements.new')}
        </Button>
      </Flex>

      {loading && <Loading active center />}
      {error && <TextError error={error} />}

      {data && announcements.length === 0 && (
        <Text color="gray.500">{t('SuperAdmin.announcements.empty')}</Text>
      )}

      {announcements.length > 0 && (
        <Card>
          <CardBody px={0}>
            <TableContainer>
              <Table size="sm">
                <Thead>
                  <Tr>
                    <Th>{t('SuperAdmin.announcements.columns.state')}</Th>
                    <Th>{t('SuperAdmin.announcements.columns.type')}</Th>
                    <Th>{t('SuperAdmin.announcements.columns.message')}</Th>
                    <Th>{t('SuperAdmin.announcements.columns.dates')}</Th>
                    <Th />
                  </Tr>
                </Thead>
                <Tbody>
                  {announcements.map((announcement) => (
                    <AdminAnnouncementRow
                      key={announcement.id}
                      announcement={announcement}
                      current={isAnnouncementCurrent(announcement, now)}
                      onEdit={() => setEditing(announcement)}
                      onDelete={() => handleDelete(announcement)}
                    />
                  ))}
                </Tbody>
              </Table>
            </TableContainer>
          </CardBody>
        </Card>
      )}

      {newModal.isOpen && (
        <AdminAnnouncementModal
          isOpen
          onClose={newModal.onClose}
          onSubmit={handleCreate}
        />
      )}

      {editing && (
        <AdminAnnouncementModal
          isOpen
          announcement={editing}
          onClose={() => setEditing(undefined)}
          onSubmit={handleUpdate(editing.id)}
        />
      )}

      {confirmElement}
    </>
  )
}
